import { useTranslation } from "react-i18next";
import type { ApiSchema } from "../../../api/apiSchema";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { CONTROL_ROW_CLASS } from "../shared/authoringStyles";
import FieldGroup from "../../../ui/forms/FieldGroup";

type Requirement = ApiSchema<"CapabilityRequirement">;

/**
 * What a route must offer to run the step (capabilities.md CAP-5): one row per
 * capability, either equal to a value or at least a number. The capability names are the
 * vocabulary the connectors report; the server checks them.
 */
export default function RequirementsEditor({ requirements, onChange }: { requirements: readonly Requirement[]; onChange: (requirements: Requirement[]) => void }) {
  const { t } = useTranslation();
  const replace = (index: number, next: Requirement | null) => {
    onChange(requirements.flatMap((requirement, position) => (position !== index ? [requirement] : next === null ? [] : [next])));
  };
  return (
    <FieldGroup legend={t("authoring.step.requirements.title")} className="space-y-1">
      {requirements.map((requirement, index) => {
        const atLeast = "at_least" in requirement;
        const value = atLeast ? String(requirement.at_least) : String(requirement.equals);
        return (
          <div key={index} className={CONTROL_ROW_CLASS}>
            <input
              className={`${FORM_COMPACT_CONTROL_CLASS} w-40 font-mono`}
              aria-label={t("authoring.step.requirements.capability")}
              value={requirement.capability}
              onChange={(event) => { replace(index, { ...requirement, capability: event.target.value }); }}
            />
            <select
              className={`${FORM_SELECT_CLASS} w-auto`}
              aria-label={t("authoring.step.requirements.rule")}
              value={atLeast ? "at_least" : "equals"}
              onChange={(event) => {
                replace(index, event.target.value === "at_least" ? { capability: requirement.capability, at_least: Number.parseInt(value, 10) || 0 } : { capability: requirement.capability, equals: true });
              }}
            >
              <option value="equals">{t("authoring.step.requirements.equals")}</option>
              <option value="at_least">{t("authoring.step.requirements.atLeast")}</option>
            </select>
            <input
              className={`${FORM_COMPACT_CONTROL_CLASS} w-28`}
              aria-label={t("authoring.step.requirements.value")}
              value={value}
              onChange={(event) => {
                const text = event.target.value;
                replace(index, atLeast ? { capability: requirement.capability, at_least: Number.parseInt(text, 10) || 0 } : { capability: requirement.capability, equals: equalsValue(text) });
              }}
            />
            <AppButton variant="ghost" size="xs" aria-label={t("authoring.step.requirements.removeNamed", { name: requirement.capability })} onClick={() => { replace(index, null); }}>
              {t("authoring.step.requirements.remove")}
            </AppButton>
          </div>
        );
      })}
      <AppButton variant="ghost" size="xs" onClick={() => { onChange([...requirements, { capability: "", equals: true }]); }}>
        {t("authoring.step.requirements.add")}
      </AppButton>
    </FieldGroup>
  );
}

/** `true`, `false` and whole numbers are read as such; anything else stays text. */
function equalsValue(text: string): boolean | number | string {
  if (text === "true" || text === "false") {
    return text === "true";
  }
  return /^-?\d+$/.test(text) ? Number(text) : text;
}
